export interface IErrorContext {
  message: string;
  stack?: string;
  url?: string;
  userAgent?: string;
}

/**
 * Empêche la boucle de rétroaction : un échec de l'appel de journalisation rejette une promesse,
 * ce qui déclenche onunhandledrejection, qui rappelle cette fonction, qui échoue à nouveau...
 * Tant qu'un envoi est en vol, les erreurs suivantes sont uniquement écrites en console.
 */
let sending = false;

/** Coupe-circuit : après plusieurs échecs consécutifs, on cesse d'appeler le serveur. */
let consecutiveFailures = 0;
const maxConsecutiveFailures = 3;

function SendErrorToBackend(error: IErrorContext, context?: any) {
    if (sending || consecutiveFailures >= maxConsecutiveFailures) {
        console.error('Client error not reported to the backend.', error);
        return;
    }

    sending = true;

    fetch('/api/log-client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            message: error.message,
            stack: error.stack,
            context: context || {},
            url: error.url,
            userAgent: error.userAgent,
        }),
    })
        .then((res: Response) => {
            consecutiveFailures = res.ok ? 0 : consecutiveFailures + 1;
        })
        .catch((e: unknown) => {
            // Impératif : sans ce catch, le rejet remonte à onunhandledrejection ci-dessous
            // et provoque une tempête de requêtes quand le serveur est indisponible.
            consecutiveFailures++;
            console.error('Failed to report a client error to the backend.', e);
        })
        .then(() => {
            sending = false;
        });
}

// Capture erreurs JS globales
const previousOnError = window.onerror;
window.onerror = function (message, source, lineno, colno, error) {
    SendErrorToBackend(
        error
            ? { message: error.message, stack: error.stack, url: source, userAgent: navigator.userAgent }
            : { message: String(message), url: source, userAgent: navigator.userAgent }
    );

    // Ne pas déposséder un gestionnaire déjà installé.
    return previousOnError ? previousOnError.call(window, message, source, lineno, colno, error) : false;
};

// Capture promesses rejetées
const previousOnUnhandledRejection = window.onunhandledrejection;
window.onunhandledrejection = function (event: PromiseRejectionEvent) {
    const reason: unknown = event.reason;
    const error: Error | undefined = reason instanceof Error ? reason : undefined;

    SendErrorToBackend({
        message: error ? error.message : typeof reason === 'string' ? reason : JSON.stringify(reason),
        stack: error?.stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
    });

    if (previousOnUnhandledRejection) previousOnUnhandledRejection.call(window, event);
};

export { SendErrorToBackend };
